import React, {Fragment} from 'react';
import SelectorGrid from "../../form/SelectorGrid/SelectorGrid";
import createEditor from "../../form/ComponentEditor";
import Box from "../../layout/Box";
import {CurrencyFormatter, optionsTemplate, UnitFormatter, DateFormatter} from "../../../utils/formatters";
import isEqual from "lodash-es/isEqual";
import ContentLoader from "../../layout/ContentLoader";
import Tabs from "../../layout/Tabs";
import TabContent from "../../layout/TabContent";
import Button from "../../form/Button";
import {getComponentConfig, loadComponent} from "../../../utils/components";
import Select from "../../form/Select";
import {get, set} from "../../../utils/dotutils";
import Collapse from "../../layout/Collapse";
import Modal from "../../layout/Modal";
import Icon from "../../form/Icon";
import Tab from "../../layout/Tab";
import classNames from "classnames";
import DateTimeInput from "../../form/DateTimeInput";
import moment from "moment";
import PeriodInput, {periodOptions} from "../../form/PeriodInput";
import RadioGroup from "../../form/RadioGroup";
import TextInput from "../../form/TextInput";
import {stringToDate} from "../../../utils/other";

const formats = [
    {
        key: "day",
        label: "nap"
    },
    {
        key: "month",
        label: "hónap"
    },
    {
        key: "quarter",
        label: "negyed év"
    },
    {
        key: "half",
        label: "fél év"
    },
    {
        key: "year",
        label: "év"
    }
];

const nameGenerator = (key, field) => {
    if (field === "base_product_id") {
        return "Termék";
    } else if (field === "product_id") {
        return "Kiadás";
    } else if (key === "productperpurchase") {
        return "Áru/Vásárlás";
    } else if (key === "tradename" || key.endsWith(".tradename") || key.endsWith("tradename.name")) {
        return "Kiadó";
    } else if (key === "series.title") {
        return "Sorozat";
    } else if (key === "manufacturer" || key.endsWith(".manufacturer") || key.endsWith("manufacturer.name")) {
        return "Gyártó";
    } else if (key === "customer" || key.endsWith(".customer") || key.endsWith("customer.name")) {
        return "Partner";
    } else if (key === "pos" || key.endsWith(".pos") || key.endsWith("pos.name")) {
        return "Eladási hely";
    } else if (key === "category.name" || key === "category") {
        return "Árutípus";
    } else if (field === "displayname") {
        return "Megnevezés";
    } else if (field === "sku") {
        return "Vonalkód";
    } else if (field === "purchases") {
        return "Vásárlások";
    } else if (field === "qty") {
        return "Darabszám";
    } else if (field === "discount") {
        return "Kedvezmény";
    } else if (field === "promotion") {
        return "Akció";
    } else if (key === "promotion.name") {
        return "Akció neve";
    } else if (key === "discount.promo_code") {
        return "Kuponkód";
    } else if (key === "discount.name") {
        return "Kedvezmény neve";
    } else if (key === "discount.value" || key === "disc_value") {
        return "Kedvezmény értéke";
    } else if (key === "discount") {
        return "Kedvezmény";
    } else if (key === "promotion.start_date") {
        return "Akció kezdete";
    } else if (key === "promotion.end_date") {
        return "Akció vége";
    } else if (key === "package_products") {
        return "Csomag tartalma";
    } else if (key === "package_sold") {
        return "Eladott csomagszám";
    } else if (field.indexOf("price") !== -1) {
        let name = "";
        if (field.indexOf("sale") !== -1) {
            name = "Eladási ár";
        } else if (field.indexOf("list") !== -1) {
            name = "Lista ár";
        }
        if (field.indexOf("avg") !== -1) {
            name = "Átlag " + name.toLowerCase();
        }
        if (field.indexOf("net") !== -1) {
            if (field.indexOf("total") !== -1) {
                name = "Nettó " + name.toLowerCase() + " összesen";
            } else {
                name = "Nettó " + name.toLowerCase();
            }
        } else if (field.indexOf("gross") !== -1) {
            if (field.indexOf("total") !== -1) {
                name = "Bruttó " + name.toLowerCase() + " összesen";
            } else {
                name = "Bruttó " + name.toLowerCase();
            }
        }
        return name;
    }
    return key;
}

const getDefaultProps = (key, field) => {
    let props = {};
    if (key === "pos" || key.endsWith(".pos") || key.endsWith("pos.name")) {
        props.storeType = "local";
    } else if (key === "manufacturer" || key.endsWith(".manufacturer") || key.endsWith("manufacturer.name")) {
        props.labelKey = "name";
    } else if (key === "customer" || key.endsWith(".customer") || key.endsWith("customer.name")) {
        props.labelKey = "name";
    }
    return props;
}

const getColumns = (view, prefix = []) => {
    let columns = {};
    Object.entries(view).map((entry) => {
        if (entry[1].constructor === {}.constructor) {
            columns = {...columns, ...getColumns(entry[1], [...prefix, entry[0]])};
        } else {
            const key = [...prefix, entry[0]].join(".");
            columns[key] = {
                header: key,
                sortable: true
            };
            if (entry[0].indexOf("price") !== -1) {
                columns[key].renderer = CurrencyFormatter;
                columns[key].aggregation = CurrencyFormatter;
                columns[key].className = "currency";
                /*columns[key].tooltip = (value, rowData, rowIndex, {rows, fieldName}) => {
                    return rowIndex > 0 ? (Math.round(value / rows[rowIndex-1][fieldName] * 10000) / 100) + "%" : "";
                }*/
            }
            if (entry[0].indexOf("qty") !== -1 || entry[0] === "purchases" || key === "productperpurchase") {
                columns[key].renderer = (value, row) => UnitFormatter(value, row.unit);
                columns[key].aggregation = (value, row) => UnitFormatter(value, row.unit);
                columns[key].className = "unit";
            }
            if (entry[0].indexOf("discount") !== -1) {
                columns[key].renderer = (value) => {
                    return <span className="percent">{(parseFloat(value || 0) * 100).toFixed(2) + "%"}</span>;
                };
            }
            if (entry[0].indexOf("date") !== -1) {
                columns[key].renderer = DateFormatter;
                columns[key].aggregation = DateFormatter;
                columns[key].className = "date";
            }
            if (columns[key].header.indexOf("discount.value") !== -1) {
                columns[key].renderer = (value, row) => {
                    return (
                        row.discount.calculation_mode == "percentage"
                        ?
                        <span className="percent">{value}%</span>
                        :
                        <span>{value} Ft</span>
                    )
                };
            }

            columns[key].header = nameGenerator(key, entry[0]) || columns[key].header;
        }
    });
    return columns;
}

const getFieldComponentConfig = async (config, field) => {
    if (typeof config === "string") {
        config = await getComponentConfig(config);
    }
    if (config.fielddefs[field]) {
        return config;
    }
    if (field.indexOf(".") !== -1) {
        const fields = field.split(".", 2);
        if (config.fielddefs[fields[0]]) {
            return getFieldComponentConfig(await getComponentConfig(config.fielddefs[fields[0]].object || config.fielddefs[fields[0]].objects), fields[1]);
        }
    }
    if (config.target) {
        return getFieldComponentConfig(await getComponentConfig(config.target), field);
    }
    // TODO adapter
}

const HISTORY_REPEAT = 3;
const PAST_REPEAT = 3;

class DateFilter extends React.Component {

    constructor(props) {
        super(props);
        this.loadDefaults();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.filterField !== this.props.filterField || this.props.component_name !== prevProps.component_name) {
            this.loadDefaults();
        }
    }

    loadDefaults = () => {
        let settings = this.props.getSettings();
        const defaults = this.props.defaults || {};
        const defaultDate = moment().format("YYYY-MM-DD");
        const field = defaults.field || get(settings, "field", this.props.filterConfig.types[this.props.filterConfig.default || 0]);
        let value = get(settings, "value") || defaults.value || [defaultDate, defaultDate];
        if (value && !Array.isArray(value)) {
            value = [value, value];
        }
        value = value.map((v) => typeof v === "string" ? moment(stringToDate(v)).format("YYYY-MM-DD") : v);
        const by = defaults.by || get(settings, "by") || "history";
        const history_repeat = defaults.history_repeat || get(settings, "history_repeat") || HISTORY_REPEAT;
        const past_repeat = defaults.past_repeat || get(settings, "past_repeat") || PAST_REPEAT;
        let other_dates = defaults.other_dates || get(settings, "other_dates") || null;
        if (Array.isArray(other_dates)) {
            other_dates = other_dates.map((dates) => dates.map((v) => typeof v === "string" ? moment(stringToDate(v)).format("YYYY-MM-DD") : v));
        }
        settings = {...settings, field, value, by, history_repeat, past_repeat, other_dates};
        this.setSettings(this.props.setSettings(settings));
    }

    setSettings = (settings) => {
        const field = get(settings, "field", this.props.filterConfig.types[this.props.filterConfig.default || 0]);
        const value = get(settings, "value") || [];
        const by = get(settings, "by") || "history";
        const compare = get(settings, "compare") || false;
        let groupField = null;
        if (compare) {
            if (by === "history") {
                groupField = field + "_year";
            } else if (by === "past") {
                const {selected, periods} = periodOptions(value[0].substr(0, 4), value[0], value[1]);
                const selectedPeriod = periods.find((period) => period.key === selected);
                if (selectedPeriod) {
                    if (["year", "month", "half", "quarter"].indexOf(selectedPeriod.type) !== -1) {
                        if (selectedPeriod.type !== "year") {
                            groupField = field + "_year_" + selectedPeriod.type;
                        } else {
                            groupField = field + "_" + selectedPeriod.type;
                        }
                    }
                }
            } else if (by === "other") {
                groupField = field + "_group";
            }
        }
        this.props.setFieldValue("groupField", groupField);
    }

    setFieldSettings = (field, value) => {
        this.setSettings(this.props.setSettings(value, field));
    }

    render() {
        let settings = this.props.getSettings();
        if (!(settings || this.props.filterConfig.required || this.props.filterConfig.visible)) {
            return null;
        }
        settings = settings || {};
        const history_repeat = get(settings, "history_repeat") || HISTORY_REPEAT;
        const past_repeat = get(settings, "past_repeat") || PAST_REPEAT;
        const value = get(settings, "value") || [];
        const from = moment(value[0], "YYYY-MM-DD");
        const to = moment(value[1], "YYYY-MM-DD");
        let other_dates = get(settings, "other_dates") || [];
        const defaultOtherDate = [from.year(from.format("YYYY") - 1).format("YYYY-MM-DD"), to.year(to.format("YYYY") - 1).format("YYYY-MM-DD")];
        const {selected, periods} = periodOptions((value[0] || (new Date()).getFullYear() + "").substr(0, 4), value[0], value[1]);
        const selectedPeriod = periods.find((period) => period.key === selected);
        return <div className="col-md-12">
            <div className="form-group">
                <div className="row">
                    <div className={classNames({"col-md-9": this.props.filterConfig.types.length > 1, "col-md-11": this.props.filterConfig.types.length <= 1})}>
                        <PeriodInput
                            year={true}
                            value={(get(settings, "value") || [])}
                            label="Dátum"
                            saveChanges={(field, value) => {
                                if (!value[0]) {
                                    this.setFieldSettings("value", null);
                                } else {
                                    if (!value[1]) {
                                        value[1] = value[0];
                                    }
                                    this.setFieldSettings("value", value);
                                }
                            }}
                        />
                    </div>
                    {this.props.filterConfig.types.length > 1 && <div className="col-md-2">
                        <Select
                            storeType="local"
                            label="Dátum mező"
                            objectValue={false}
                            options={this.props.filterConfig.types.map((field) => ({field, label: ((field) => {
                                    switch (field) {
                                        case "created_at": return "Rögzítés időpontja";
                                        case "transaction_date": return "Tranzakciós dátum";
                                    }
                                })(field)}))}
                            {...this.props.filterConfig}
                            valueKey="field"
                            labelKey="label"
                            value={get(settings, "field", this.props.filterConfig.types[this.props.filterConfig.default || 0])}
                            saveChanges={(f, field) => {
                                this.setFieldSettings("field", field);
                            }}
                        />
                    </div>}
                    <div className="col-md-1">
                        <label className="control-label">&nbsp;</label>
                        <Button
                            block={true}
                            color={settings.compare ? "primary" : "default"}
                            tooltip="Összehasonlítás"
                            icon={{lib: "fas", name: "chart-bar"}}
                            onClick={() => this.setFieldSettings("compare", !settings.compare)}
                        />
                    </div>
                </div>
            </div>
            <Collapse collapsed={!!settings.compare}>
                <div>
                    <div className="row">
                        {get(settings, "by") === "history" && <div className="col-md-2">
                            <TextInput
                                type="number"
                                label={"Az elmúlt " + (history_repeat || "x") + " év"}
                                value={(get(settings, "history_repeat") || null)}
                                saveChanges={(field, history_repeat) => {
                                    this.setFieldSettings("history_repeat", history_repeat);
                                }}
                            />
                        </div>}
                        {get(settings, "by") === "past" && <div className="col-md-2">
                            <TextInput
                                type="number"
                                label={"Előző " + past_repeat + " " + ((formats.find((f) => (selectedPeriod || {}).type === f.key) || {}).label || "nap")}
                                value={(get(settings, "past_repeat") || null)}
                                saveChanges={(field, past_repeat) => {
                                    this.setFieldSettings("past_repeat", past_repeat);
                                }}
                            />
                        </div>}
                        <div className="col-md-6">
                            <RadioGroup
                                objectValue={false}
                                options={[
                                    {
                                        key: "history",
                                        label: "Éves bontás"
                                    },
                                    {
                                        key: "past",
                                        label: "Előző időszak"
                                    },
                                    {
                                        key: "other",
                                        label: "Szabadon választott"
                                    }
                                ]}
                                valueKey="key"
                                labelKey="label"
                                value={(get(settings, "by") || "history")}
                                saveChanges={(field, by) => {
                                    this.setFieldSettings("by", by);
                                }}
                                label="Összehasonlítás alapja"
                            />
                        </div>
                        <div className="col-md-4">
                            {get(settings, "by") === "other" && <div>{
                                other_dates.find((date) => {
                                    date = date || defaultOtherDate;
                                    return moment(date[0], "YYYY-MM-DD").diff(moment(date[1], "YYYY-MM-DD"), 'days') !== moment(value[0], "YYYY-MM-DD").diff(moment(value[1], "YYYY-MM-DD"), 'days');
                                }) ? <div className="label label-danger">Eltérő időszakokat hasonlítasz össze!</div> : null
                            }</div>}
                        </div>
                    </div>
                    {get(settings, "by") === "other" && <Fragment>
                        {other_dates.map((date, idx) => {
                            return <div key={idx} className="row">
                                <div className="col-md-11">
                                    <div className="form-group">
                                        <PeriodInput
                                            year={true}
                                            value={date || defaultOtherDate}
                                            label="Dátum"
                                            saveChanges={(field, value) => {
                                                const new_dates = [...other_dates];
                                                if (!value[0]) {
                                                    new_dates[idx] = null;
                                                } else {
                                                    if (!value[1]) {
                                                        value[1] = value[0];
                                                    }
                                                    new_dates[idx] = value;
                                                }
                                                this.setFieldSettings("other_dates", new_dates);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-1">
                                    <div className="form-group">
                                        <label className="control-label">&nbsp;</label>
                                        <Button
                                            icon={{lib: "fa", name: "times"}}
                                            color="danger"
                                            block={true}
                                            onClick={() => {
                                                const new_dates = [...other_dates];
                                                new_dates.splice(idx, 1);
                                                this.setFieldSettings("other_dates", new_dates);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>;
                        })}
                        <div className="form-group">
                            <Button
                                color="default"
                                block={true}
                                icon={{lib: "fas", name: "plus"}}
                                onClick={() => this.setFieldSettings("other_dates", [...other_dates, defaultOtherDate])}
                                tooltip="További dátumok hozzáadása"
                            />
                        </div>
                    </Fragment>}
                </div>
            </Collapse>
        </div>;
    }
}

class PosFilter extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            options: [],
            isLoading: true
        }
        this.loadDefaults();
        this.loadOptions(false);
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.filterField !== this.props.filterField || this.props.component_name !== prevProps.component_name) {
            this.loadDefaults();
            this.loadOptions();
        }
    }

    loadOptions = (state = true) => {
        const c = () => {
            let load = 2;
            this.pointOfSalesRequest && this.pointOfSalesRequest.abort();
            this.pointOfSalesRequest = loadComponent("componentlist", {
                component: "PointOfSale",
                itemsperpage: 0,
                view: ["id","name"],
            });
            this.pointOfSalesRequest.then((list) => {
                load--;
                this.setState({options: [...this.state.options, ...list.items.map((pos) => ({...pos, id: "pos|" + pos.id, type: "pos"}))].sort((a,b) => a.id.indexOf("group|")-b.id.indexOf("group|")), isLoading: !!load});
            });
            this.pointOfSalesGroupsRequest && this.pointOfSalesGroupsRequest.abort();
            this.pointOfSalesGroupsRequest = loadComponent("componentlist", {
                component: "PointOfSaleGroup",
                itemsperpage: 0,
                view: ["id","name"],
            });
            this.pointOfSalesGroupsRequest.then((list) => {
                load--;
                this.setState({options: [...this.state.options, ...list.items.map((pos) => ({...pos, id: "group|" + pos.id, type: "group"}))].sort((a,b) => a.id.indexOf("group|")-b.id.indexOf("group|")), isLoading: !!load});
            });
        }
        if (state) {
            this.setState({options: [], isLoading: true}, () => {
                c();
            });
        } else {
            c();
        }
    }

    loadDefaults = () => {
        let settings = this.props.getSettings();
        const defaults = this.props.defaults || {};
        const value = get(settings, "value") || defaults.value || null;
        settings = {...settings, value};
        this.props.setSettings(settings);
    }

    setFieldSettings = (field, value) => {
        this.props.setSettings(value, field);
    }

    render() {
        const filterField = this.props.getFilterField();
        const field = filterField.split(".");
        const currentField = field.pop();
        const settings = this.props.getSettings();
        let value = get(settings, "value");
        let type = null;
        if (Array.isArray(value) && value.length) {
            type = value[0].split("|")[0];
        }
        return (
            <div className="col-md-3">
                <div className="form-group">
                    <Select
                        storeType="local"
                        label={<span className="stat-label">{nameGenerator(filterField, currentField) || filterField} {!(this.props.filterConfig.required || this.props.filterConfig.visible) && <Button
                            icon={{lib: "fa", name: "times"}}
                            color="danger"
                            size="xs"
                            className="pull-right label-button"
                            onClick={() => {
                                this.props.setFieldValue("parameters", null);
                                this.props.setFieldValue("filter", null);
                            }}
                        />}</span>}
                        objectValue={false}
                        isClearable={true}
                        isMulti={true}
                        optionsTemplate={(data) => {
                            if (data.id.indexOf("group|") === 0) {
                                return <b><i>{data.name}</i></b>;
                            } else {
                                return data.name;
                            }
                        }}
                        isLoading={this.state.isLoading}
                        options={this.state.options.filter((o) => type ? o.id.indexOf(type) === 0 : true)}
                        {...getDefaultProps(filterField, currentField)}
                        required={this.props.filterConfig.required}
                        {...(this.props.filterConfig.props || {})}
                        valueKey="id"
                        labelKey="name"
                        value={value}
                        saveChanges={(f, value) => {
                            let values = value;
                            if (values.length) {
                                const type = values[0].split("|")[0];
                                values = values.filter((v) => v.indexOf(type) === 0).sort();
                            } else {
                                values = null;
                            }
                            this.setFieldSettings("value", values)
                        }}
                    />
                </div>
            </div>
        );
    }
}

class Filter extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            config: null,
            fieldConfig: null
        }
        this.loadConfig();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (prevProps.filterField !== this.props.filterField || this.props.component_name !== prevProps.component_name) {
            this.loadConfig();
        }
    }

    loadConfig = () => {
        this.abort && this.abort();
        let aborted = false;
        this.abort = () => {
            aborted = true;
        }
        this.state.config && this.setState({config: null});
        getFieldComponentConfig(this.props.component_name, this.props.filterField).then((config) => {
            if (!aborted) {
                this.setState({config: false}, () => {
                    if (config) {
                        const field = this.props.filterField.split(".");
                        const currentField = field.pop();
                        const fieldComponent = config.fielddefs[currentField].object || config.fielddefs[currentField].objects;
                        if (fieldComponent) {
                            getComponentConfig(fieldComponent).then((fieldConfig) => {
                                if (!aborted) {
                                    this.setState({fieldConfig}, () => this.loadDefaults());
                                }
                            })
                        } else {
                            this.loadDefaults();
                        }
                    } else {
                        this.loadDefaults();
                    }
                });
            }
        })
    }

    loadDefaults = () => {
        if (this.state.fieldConfig && this.state.fieldConfig.componentname !== "PointOfSale" && this.props.defaults) {
            const settings = this.getSettings() || {};
            const defaults = this.props.defaults || {};
            settings.value = defaults.value || get(settings, "value");
            if (settings.value && !Array.isArray(settings.value)) {
                settings.value = [settings.value];
            }
            this.setSettings(settings);
        }
    }

    getSettings = () => {
        return get(this.props.values, "parameters");
    }

    setSettings = (value, field = null) => {
        let settings = this.getSettings() || {};
        if (field) {
            set(settings, field, value);
            this.props.setFieldValue("parameters." + field, value);
        } else {
            settings = value;
            this.props.setFieldValue("parameters", value);
        }
        if(this.props.filterConfig.fieldtype === "Date") {
            return settings;
        }
    }

    getFilterField = () => {
        return this.props.filterField;
    }

    render() {
        if (this.state.config === null) return null;
        const filterField = this.getFilterField();
        const field = filterField.split(".");
        const currentField = field.pop();
        const settings = this.getSettings();
        if (this.state.fieldConfig) {
            if (!(settings || this.props.filterConfig.required || this.props.filterConfig.visible)) {
                return null;
            }
            if (this.state.fieldConfig.componentname === "PointOfSale") {
                return <PosFilter
                    {...this.props}
                    fieldConfig={this.state.fieldConfig}
                    setSettings={this.setSettings}
                    getSettings={this.getSettings}
                    getFilterField={this.getFilterField}
                />;
            }
            return <div className="col-md-3">
                <div className="form-group">
                    <Select
                        storeType="remote"
                        label={<span className="stat-label">{nameGenerator(filterField, currentField) || filterField} {!(this.props.filterConfig.required || this.props.filterConfig.visible) && <Button
                            icon={{lib: "fa", name: "times"}}
                            color="danger"
                            size="xs"
                            className="pull-right label-button"
                            onClick={() => {
                                this.props.setFieldValue("parameters", null);
                                this.props.setFieldValue("filter", null);
                            }}
                        />}</span>}
                        objectValue={false}
                        isClearable={true}
                        isMulti={true}
                        optionsTemplate={optionsTemplate(this.state.fieldConfig.componentname)}
                        component={this.state.fieldConfig.componentname}
                        {...getDefaultProps(filterField, currentField)}
                        required={this.props.filterConfig.required}
                        {...(this.props.filterConfig.props || {})}
                        valueKey={this.state.fieldConfig.idfield}
                        value={get(settings, "value")}
                        saveChanges={(f, value) => {
                            this.setSettings(value.length ? value : null, "value");
                        }}
                    />
                </div>
            </div>;
        } else if (this.props.filterConfig.fieldtype === "Date") {
            return (
                <DateFilter
                    {...this.props}
                    setSettings={this.setSettings}
                    getSettings={this.getSettings}
                    getFilterField={this.getFilterField}
                />
            );
        } else if (currentField === "product_id" || currentField === "base_product_id") {
            if (!(settings || this.props.filterConfig.required || this.props.filterConfig.visible)) {
                return null;
            }
            return <div className="col-md-3">
                <div className="form-group">
                    <Select
                        storeType="remote"
                        label={<span className="stat-label">{nameGenerator(filterField, currentField) || filterField} {!(this.props.filterConfig.required || this.props.filterConfig.visible) && <Button
                            icon={{lib: "fa", name: "times"}}
                            color="danger"
                            size="xs"
                            className="pull-right label-button"
                            onClick={() => {
                                this.props.setFieldValue("parameters", null);
                                this.props.setFieldValue("filter", null);
                            }}
                        />}</span>}
                        objectValue={false}
                        isClearable={true}
                        isMulti={true}
                        optionsTemplate={optionsTemplate("product")}
                        component="product"
                        {...getDefaultProps(filterField, currentField)}
                        required={this.props.filterConfig.required}
                        {...(this.props.filterConfig.props || {})}
                        valueKey={currentField === "base_product_id" ? "base_product_id" : "id"}
                        value={get(settings, "value")}
                        saveChanges={(f, value) => {
                            this.setSettings(value.length ? value : null, "value");
                        }}
                    />
                </div>
            </div>;
        }
        return null;
    }
}

class Filters extends React.Component {

    state = {
        openMoreModal: false,
        addMoreFilter: []
    }

    getFilters = () => {
        return this.props.filters || {};
    }

    getFilter = () => {
        let required = false;
        const filters = this.getFilters();
        const filtersValues = get(this.props.settings, "filters") || {};
        const search = Object.entries(filters || {}).map((entry, idx) => {
            const values = filtersValues[entry[0].replace(/\./g, "/")] || {};
            return {groupField: values.groupField};
        }).filter((v) => !!v).reduce((all, current) => {
            return {
                groupField: all.groupField || current.groupField
            };
        }, {});
        const parameters = {};
        Object.entries(filtersValues).map((entry) => {
            parameters[entry[0].replace(/\//g, ".")] = entry[1].parameters;
        });
        search.parameters = parameters;
        return required ? null : search;
    }

    onFilter = () => {
        this.props.onFilter(this.getFilter());
    }

    render() {
        const filters = this.getFilters();
        const newFilter = this.getFilter();
        const same = JSON.stringify(get(this.props.settings, "search")) === JSON.stringify(newFilter) || newFilter === null;
        let addMore = [];
        return (
            <Fragment>
                <div className="row">
                    {Object.entries(filters).sort((a,b) => {
                        if ((a[1].required || a[1].visible) && (b[1].required || b[1].visible)) {
                            return a[1].sort - b[1].sort;
                        }
                        if (!(a[1].required || a[1].visible) && !(b[1].required || b[1].visible)) {
                            return b[1].sort - a[1].sort;
                        }
                        if ((a[1].required || a[1].visible) && !(b[1].required || b[1].visible)) {
                            return -1;
                        }
                        if(!(a[1].required || a[1].visible) && (b[1].required || b[1].visible)) {
                            return 1;
                        }
                        return 0;
                    }).map((entry, idx) => {
                        const key = entry[0].replace(/\./g, "/");
                        const filterConfig = entry[1];
                        if (!(get(this.props.settings, "filters." + key + ".parameters") || filterConfig.required || filterConfig.visible)) {
                            addMore.push(key);
                        }
                        return <Filter
                            key={idx}
                            {...this.props}
                            filterField={entry[0]}
                            filterConfig={filterConfig}
                            defaults={(this.props.values.default_filters || {})[entry[0]]}
                            values={get(this.props.settings, "filters." + key)}
                            setFieldValue={(field, value) => {
                                this.props.setFieldValue("filters." + key + "." + field, value);
                            }}
                        />;
                    })}
                    <div className="col-md-2">
                        <div className="row">
                            {!!addMore.length && <div className="col-md-6">
                                <div className="form-group">
                                    <label className="control-label">&nbsp;</label>
                                    <Modal
                                        header="További lehetséges szűrők"
                                        size="medium"
                                        state={this.state.openMoreModal}
                                        buttons={[
                                            {
                                                text: "Mehet",
                                                props: {color: "primary", className: "pull-right", disabled: !this.state.addMoreFilter.length},
                                                onClick: async () => {
                                                    this.state.addMoreFilter.map((key) => {
                                                        set(this.props.settings, "filters." + key + ".parameters", {});
                                                        this.props.setFieldValue("filters", this.props.settings.filters);
                                                    });
                                                    this.setState({openMoreModal: false, addMoreFilter: []});
                                                }
                                            }, {
                                                text: "Mégse",
                                                props: {color: "default"},
                                                className: "pull-right",
                                                onClose: true,
                                                onClick: async () => {
                                                    this.setState({openMoreModal: false, addMoreFilter: []});
                                                }
                                            }
                                        ]}
                                    >
                                        {addMore.map((key, idx) => {
                                            return <Fragment key={idx}><Button
                                                color={this.state.addMoreFilter.indexOf(key) !== -1 ? "primary" : "default"}
                                                onClick={() => this.setState({addMoreFilter: this.state.addMoreFilter.indexOf(key) !== -1 ? [...this.state.addMoreFilter].filter((k) => k !== key) : [...this.state.addMoreFilter, key]})}
                                            >
                                                {nameGenerator(key.replace(/\//g, "."), key.replace(/\//g, ".").split(".").pop())}
                                            </Button> </Fragment>;
                                        })}
                                    </Modal>
                                    <Button
                                        color="default"
                                        block={true}
                                        icon={{lib: "fas", name: "plus"}}
                                        onClick={() => this.setState({openMoreModal: true})}
                                        tooltip="További lehetséges szűrők"
                                    />
                                </div>
                            </div>}
                            <div className={classNames({"col-md-6": !!addMore.length, "col-md-12": !addMore.length})}>
                                <label className="control-label">&nbsp;</label>
                                <div className="form-group">
                                    <Button
                                        color={same ? "default" : "warning"}
                                        disabled={same}
                                        block={true}
                                        icon={{lib: "fa", name: "search"}}
                                        onClick={this.onFilter}
                                        tooltip="Szűrés"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

class StatisticsTab extends React.Component {

    getStatName = () => {
        return this.props.values[this.props.componentConfig.idfield];
    }

    getStatSettings = () => {
        return this.props.values;
    }

    render() {
        const statName = this.getStatName();

        const statSettings = this.getStatSettings();
        const columns = getColumns(statSettings.stat_view);
        const filters = statSettings.filters || {};
        const search = JSON.parse(JSON.stringify(get(this.props.settings, "search") || null));
        let parameters = null;

        if (search) {
            parameters = search.parameters;
            delete search.parameters;
            delete search.filter;
        }

        return <div className="statistics">
            <div className="row">
                <div className="col-md-12">
                    <Filters
                        {...this.props}
                        component_name={this.props.values.component_name}
                        filters={filters}
                        settings={this.props.settings}
                        statSettings={statSettings}
                        onFilter={(search) => {
                            this.props.setFieldValue("search", search);
                        }}
                    />
                </div>
                <div className="col-md-12">
                    {search && <SelectorGrid
                        option={this.props.componentConfig.componentname}
                        component={this.props.componentConfig.componentname}
                        exports={[{as: "excel", label: "Excel", icon: "far fa-file-excel", addFilters: true}]}
                        pageSize={this.props.values.limit !== null && this.props.values.limit !== undefined ? parseInt(this.props.values.limit) : this.props.values.limit}
                        reqparams={{
                            stat: true,
                            id: statName,
                            filters: parameters
                        }}
                        visibleColumns={statSettings.visibleColumns}
                        columns={columns}
                        {...search}
                        rowGroupHeaderTemplate={search.groupField ? (row) => get(row, search.groupField) : null}
                        rowGroupFooterTemplate={() => {}}
                    />}
                </div>
            </div>
        </div>;
    }
}


class StatEditorComponent extends React.Component {

    state = {
        isLoading: false,
    }

    render() {
        if (!this.props.values[this.props.componentConfig.idfield]) {
            return null;
        }
        if (this.state.isLoading) {
            return <ContentLoader />;
        }

        return (
            <Box>
                <div className="row">
                    <div className="col-md-12 stats">
                        <StatisticsTab
                            values={this.props.values}
                            settings={this.props.values.settings}
                            componentConfig={this.props.componentConfig}
                            setFieldValue={(field, value) => {
                                const values = JSON.parse(JSON.stringify(this.props.values));
                                set(values, "settings." + field, value);
                                this.props.setInitialValues(values);
                                this.props.setFieldValue("settings." + field, value);
                            }}
                        />
                    </div>
                </div>
            </Box>
        );
    }
}

const Editors = {};

export default (props) => {
    if (!props.id) return null;
    props = {...props};
    let Editor;
    if (!Editors[props.id]) {
        Editors[props.id] = Editor = createEditor({
            componentName: "Stat",
            WrappedComponent: StatEditorComponent,
            editor: "Stat" + props.id
        });
    } else {
        Editor = Editors[props.id];
    }
    if (props.setContent) {
        props.setContent = (content, props) => props.setContent("Stat", props)
    }
    return <Editor {...props} />;
};